import { getMerchantIdFromStorage } from './sessionStorage';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import i18n from 'i18next';
import { Locale } from './i18n/locale';
import { FetchArgs } from '@reduxjs/toolkit/query/react';
import { AppMarketContextType } from '../models/common';

export type Namespace = 'cos/v1' | 'v2' | 'v3' | 'billing/v3' | 'inventory' | 'clovercapital' | 'partnervas/v3' | 'oloservice/v1' | 'disputes/v1' | 'oauth';
export type BaseResource = 'accounts' | 'app_categories' | 'suggest' | 'apps' | 'dashboard' | 'developers' | 'enterprises' | 'merchant' | 'merchants' | 'm' | 'app_collections' | 'resellers' | 'server_feature' | 'merchant_plans' | 'partner' | 'routing_number' | 'search' | 'v3' | 'internal' | 'authorize';
export type NestedResource = string;
export type PublicBaseResource = '';

export type SortDirection = 'ASC' | 'DESC';
export type Sort = {
  name: string | undefined ;
  direction?: SortDirection;
};
export type BadRequestData = {
  data: string;
  statusCode: number | string;
};
export type GoodRequest<T> = {
  data: T;
  statusCode: number;
}
export type PostRequestResponseType<T> = GoodRequest<T> | BadRequestData;
export type CustomQuery = Obj<string>;

export type ApiQuery = {
  path?: string;
  namespace: Namespace;
  baseResource: BaseResource | PublicBaseResource;
  baseResourceId?: Uuid;
  nestedResources: NestedResource[];
  nestedResourcesIds: Uuid[];
  filters: string[];
  finds: string[];
  expands: string[];
  sorts: Sort[];
  customQueries: CustomQuery[];
  offset?: number;
  limit?: number;
  orderBy?: string;
  noCache?: boolean;
};

export type AppMarketContextForApis = {contextType: AppMarketContextType, id: Uuid}

// set language header to localize error messages and /v2 data endpoints
const setLanguageHeader = (header: Obj<any> = {}): Record<string, string> => {
  if (i18n.language) {
    const locale = Locale.normalizeLanguageTag(i18n.language);
    header['Accept-Language'] = locale;
  }
  return header;
}

const attachCsrfTokenToHeader = (header: Obj<any>= {}): Record<string, string> => {
  const csrfToken = sessionStorage.getItem('csrfToken');
  if (csrfToken) {
    header['X-CSRF-Token'] = csrfToken;
  }
  return header;
}

const getAxiosHeadersConfig = (headers: Obj<any> = {}, config: AxiosRequestConfig = {}) => {
  setLanguageHeader(headers);

  // Set request headers if there are any
  if (Object.keys(headers).length) {
    config.headers = headers;
  }

  return config;
};

export const getAxiosRequestConfig = (headers: Obj<any> = {}, config?: AxiosRequestConfig) => {
  attachCsrfTokenToHeader(headers);

  return getAxiosHeadersConfig(headers, config);
};

export const getAppMarketContextForApi = (isV2Api?: boolean): AppMarketContextForApis => {
  const merchantId = getMerchantIdFromStorage();
  let contextType :AppMarketContextType = '';
  if(merchantId){
    contextType = isV2Api ? `merchant` : `merchants`
  }
  return {
    contextType: contextType,
    id: merchantId
  }
}


const getDefaultApiQuery = (isV2Api?: boolean): ApiQuery => {
  const {contextType, id} = getAppMarketContextForApi(isV2Api);
  const baseResource = contextType as BaseResource;
  const baseResourceId = id;

  return {
    namespace: 'v3',
    baseResource,
    baseResourceId,
    nestedResources: [],
    nestedResourcesIds: [],
    filters: [],
    finds: [],
    expands: [],
    sorts: [],
    customQueries: [],
  };
};

export const url = (params: Partial<ApiQuery> | string) : string => {
  // If a key was passed in that was explicitly set to `undefined`, it would get spread into the query and break
  // e.g. 'filters: undefined' -> `filters.map` would incorrectly appear valid
  const curatedParams = (typeof params === 'string' ? { path: params } : params);
  const paramsWithoutUndefinedValues : Partial<ApiQuery> = {} as Partial<ApiQuery>;
  Object.entries(curatedParams).forEach(([key, val]) => {
    if (val !== undefined) {
      let field = paramsWithoutUndefinedValues[key as keyof typeof paramsWithoutUndefinedValues];
      if (field !== undefined) {
        field = val;
      }
    }
  });

  const isV2Api = curatedParams.namespace === "v2";
  const query: ApiQuery = {
    ...getDefaultApiQuery(isV2Api),
    ...curatedParams,
  };

  const { namespace, baseResource, baseResourceId, nestedResources, nestedResourcesIds, path } = query;
  let baseUrl = path;

  if (!baseUrl) {
    baseUrl = `/${namespace}${baseResource ? `/${baseResource}`:''}${baseResourceId ? `/${baseResourceId}` : ''}`;
    nestedResources.forEach((nestedResource, index) => {
      const id = nestedResourcesIds[index];

      baseUrl += `/${nestedResource}${id ? `/${id}` : ''}`;
    });
  }

  const filterStrings = query.filters.map(filter => `filter=${filter}`);
  const findStrings = query.finds.map(find => `find=${encodeURIComponent(find)}`);
  const expandStrings = query.expands.map(expand => `expand=${expand}`);
  const sortStrings = query.sorts.map(sort => `orderBy=${sort.name} ${sort.direction || 'ASC'}`);
  const customQueryStrings = (query.customQueries || []).map(customQuery => {
    const key = Object.keys(customQuery)[0];
    const value = customQuery[key];

    return `${key}=${value}`;
  });

  const conditions: string[] = [
    ...filterStrings,
    ...findStrings,
    ...expandStrings,
    ...sortStrings,
    ...customQueryStrings,
  ];

  if (query.offset !== undefined) conditions.push(`offset=${query.offset}`);
  if (query.limit) conditions.push(`limit=${query.limit}`);
  if (query.noCache) conditions.push(`_=${Math.random()}`);

  const queryString = `?${conditions.join('&')}`;

  return `${baseUrl}${queryString.replace(/\?$/, '')}`;
};

const apiNeedsCsrfToken = (method: string = ''): Maybe<boolean> => {
  const methodsNeedingTokenMap: Record<string, boolean> = {
    POST: true,
    DELETE: true
  }

  return methodsNeedingTokenMap[method];
}

//Appmarket api slice uses Fetch for the base query
export const getFetchArgs = (query: Partial<ApiQuery>, method?: string, body?: any): FetchArgs => {
  let fetchArgs: FetchArgs = {
    url: url(query),
    method: method ? method : 'GET',
    headers: {}
  };

  if (body) fetchArgs.body = body;
  setLanguageHeader(fetchArgs.headers);
  if (apiNeedsCsrfToken(method)) {
    attachCsrfTokenToHeader(fetchArgs.headers);
  }

  return fetchArgs;
};

const getAxiosError = (error: AxiosError): BadRequestData => {
  let errMessage = error?.response?.data.message,
    status = error?.response?.status;
  return {
    data: errMessage || '',
    statusCode: status || 0
  }
}

const transFormAxiosSuccessfulResponse = <T>(response: AxiosResponse<T>): PostRequestResponseType<T> => {
  if (response.status === 200 && response.data) {
    return {
      data: response.data,
      statusCode: response.status
    }
  }else {
    return {
      data: '',
      statusCode: response.status || 0
    }
  }
}

export const post = async <T = any>(query: Partial<ApiQuery> | string, data = {}, requestHeaders?: Obj<any>, config?: AxiosRequestConfig): Promise<PostRequestResponseType<T>> => {
  return axios.post(url(query), data, getAxiosRequestConfig(requestHeaders, config))
    .then((response: AxiosResponse<T>) => {
      return transFormAxiosSuccessfulResponse<T>(response);
    }).catch((error: AxiosError) => {
      return getAxiosError(error);
  });

};

export const remove = async <T = AxiosResponse>(query: Partial<ApiQuery> | string, data?: {}, requestHeaders?: Obj<any>, config?: AxiosRequestConfig): Promise<PostRequestResponseType<T>> => {
  return axios.delete(url(query), { ...getAxiosRequestConfig(requestHeaders, config), data })
    .then((response: AxiosResponse<T>) => {
      return transFormAxiosSuccessfulResponse<T>(response);
    }).catch((error: AxiosError) => {
      return getAxiosError(error);
    });
};

